<template>
  <div>
    <Title :pages="[{ icon: 'circle', page: page_title.toUpperCase() }]"></Title>

    <v-container fluid>
      <v-btn v-if="create_access" color="primary" class="mb-5" dark @click="dialog_nueva = true">Nueva conciliación</v-btn>

      <v-card v-if="list_access" class="mb-5">
        <v-card-text>
          <v-row align="end">
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="filter_account"
                :items="money_accounts"
                item-text="name"
                item-value="id"
                autocomplete="off"
                label="Cuenta"
                clearable
                hide-details
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete v-model="filter_state" :items="states" item-text="name" item-value="id" autocomplete="off" label="Estado" clearable hide-details></v-autocomplete>
            </v-col>
            <v-col cols="12" md="5" class="text-right">
              <v-btn color="info" @click="loadList(service)">Buscar</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card v-if="list_access">
        <v-card-title>
          <span class="text-uppercase">{{ page_title }}</span>
          <v-btn icon small @click="dialog_help = true" class="ml-2" title="Ayuda">
            <v-icon color="grey darken-1">help_outline</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-text-field v-model="search" append-icon="search" label="Buscar" single-line hide-details></v-text-field>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="list"
          :search="search"
          :loading="loading"
          loading-text="Cargando..."
          :options="{ itemsPerPage: 15 }"
          :footer-props="{ itemsPerPageOptions: [5, 10, 15, 50, -1] }"
        >
          <template v-slot:item="props">
            <tr>
              <td class="font-weight-medium">{{ props.item.label }}</td>
              <td>
                <div>{{ props.item.money_account.name }}</div>
                <div class="caption grey--text" v-if="props.item.money_account.bank">{{ props.item.money_account.bank.name }}</div>
              </td>
              <td>{{ formatUtils.formatDateString(props.item.date_from) }} – {{ formatUtils.formatDateString(props.item.date_to) }}</td>
              <td>
                <v-chip small :color="stateColor(props.item.state.id)" text-color="white">{{ props.item.state.name }}</v-chip>
              </td>
              <td style="min-width: 140px">
                <div class="caption">{{ props.item.progress.conciliados }} / {{ props.item.progress.total }}</div>
                <v-progress-linear :value="progressValue(props.item)" color="green" height="5" rounded></v-progress-linear>
              </td>
              <td class="text-right">{{ formatUtils.formatMoney(props.item.saldo_final_banco) }}</td>
              <td>{{ props.item.user ? props.item.user.email.split("@")[0] : "-" }}</td>
              <td>{{ formatUtils.formatDateTimeString(props.item.closed_at || props.item.last_saved_at) }}</td>
              <td class="text-center px-0" style="min-width: 90px">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small color="blue" class="mr-2" @click="open(props.item)">{{ props.item.state.id === "B" ? "play_arrow" : "remove_red_eye" }}</v-icon>
                  </template>
                  <span>{{ props.item.state.id === "B" ? "Continuar" : "Ver" }}</span>
                </v-tooltip>
                <v-tooltip bottom v-if="delete_access && props.item.state.id === 'B'">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small color="red" @click="deleteItem(props.item)">delete</v-icon>
                  </template>
                  <span>Descartar borrador</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
          <template v-slot:no-results>
            <span class="grey--text">No hay resultados para «{{ search }}»</span>
          </template>
        </v-data-table>
      </v-card>

      <DialogDelete :dialog_delete="dialog_delete" @updateDialogDelete="updateDialogDelete" @deleteItemConfirm="deleteItemConfirm" />
      <ModalHelp :show="dialog_help" @close="dialog_help = false" />
      <DialogNuevaConciliacion v-if="create_access" :dialog="dialog_nueva" :money_accounts="money_accounts" @close="dialog_nueva = false" @discarded="loadList(service)" />
    </v-container>
  </div>
</template>

<script>
import Constant from "@/constants/sections";
import { conciliacionBancariaService } from "@/libs/ws/conciliacionBancariaService";
import { generalService } from "@/libs/ws/generalService";
import { formatUtils } from "@/libs/utils/formatUtils";
import GenericMixin from "@/mixins/GenericMixin.vue";
import RolMixin from "@/mixins/RolMixin.vue";
import Title from "@/components/Title.vue";
import DialogDelete from "@/components/DialogDelete.vue";
import ModalHelp from "./components/ModalHelp.vue";
import DialogNuevaConciliacion from "./components/DialogNuevaConciliacion.vue";

export default {
  components: { Title, DialogDelete, ModalHelp, DialogNuevaConciliacion },
  mixins: [GenericMixin, RolMixin],
  data() {
    return {
      formatUtils: formatUtils,
      section: Constant.SECTION_CONCILIACIONES_BANCARIAS,
      service: conciliacionBancariaService,
      page_title: "Conciliación bancaria",
      dialog_help: false,
      dialog_nueva: false,
      money_accounts: [],
      states: [],
      filter_account: null,
      filter_state: null,
      headers: [
        { text: "Número", align: "left", sortable: true, value: "number" },
        { text: "Cuenta", align: "left", sortable: true, value: "money_account.name" },
        { text: "Período", align: "left", sortable: true, value: "date_from" },
        { text: "Estado", align: "left", sortable: true, value: "state.name" },
        { text: "Avance", align: "left", sortable: false, value: "" },
        { text: "Saldo banco", align: "right", sortable: true, value: "saldo_final_banco" },
        { text: "Usuario", align: "left", sortable: false, value: "" },
        { text: "Últ. modificación", align: "left", sortable: false, value: "" },
        { text: "Acción", align: "center", sortable: false, value: "" },
      ],
    };
  },
  mounted() {
    this.loadList(this.service);
    generalService.setSection(Constant.SECTION_MONEY_ACCOUNTS);
    generalService.filter({ account_type: "B" }).then((r) => { this.money_accounts = r.result; }).catch(() => {});
    this.service.params().then((r) => { this.states = r.result ? r.result.states : []; }).catch(() => {});
  },
  methods: {
    getFilterParams() {
      return { money_account_id: this.filter_account, state: this.filter_state };
    },
    open(item) {
      this.$router.push("/" + this.section + "/" + item.id);
    },
    stateColor(state) {
      return { B: "orange darken-1", C: "green darken-1", A: "grey" }[state] || "grey";
    },
    progressValue(item) {
      return item.progress && item.progress.total ? (item.progress.conciliados * 100) / item.progress.total : 0;
    },
    deleteItemConfirm() {
      this.delete(this.service);
      this.dialog_delete = false;
    },
  },
};
</script>
